import { useEffect, useState, type FormEvent } from "react";
import {
  ApiError,
  EnvelopeDownloadError,
  EvidenceFetchError,
  PackageDownloadError,
  api,
  downloadReceiptEnvelope,
  downloadReceiptPackage,
  fetchEvidenceContent,
  type ReceiptEnvelope,
  type ReceiptRetention
} from "../api/client";
import {
  inferVerificationLevel,
  isWitnessed,
  levelBadgeLabel,
  witnessedBadgeLabel,
  hasApproval,
  hasCounterparty
} from "../lib/verification-level";
import { evidenceIsFetchable, verifyEvidenceBytes } from "../lib/evidence";
import { VerificationLevelLegend } from "../components/VerificationLevelLegend";

type EvidenceReference = ReceiptEnvelope["evidence_references"][number];

// TODO: cover with component tests once the dashboard adopts a React
// test harness. Behaviors to cover: header renders parsed fields for a
// known envelope, the 410 branch shows the envelope-not-stored message,
// the 503 branch shows the storage-not-configured message, the 404
// branch shows receipt-not-found, the raw JSON section renders the
// pretty-printed envelope.
//
// The action buttons (Download, Verify) ship in PR C; the row at the
// top of the structured summary is left intentionally empty so the
// layout is stable when those buttons arrive.

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; envelope: ReceiptEnvelope }
  | { kind: "missing" }
  | { kind: "not_stored" }
  | { kind: "not_configured" }
  | { kind: "error"; message: string };

type DownloadKind = "envelope" | "zip" | "tar.gz";

// Three download options: the envelope-only JSON (existing endpoint) and
// the full .sequesign package as ZIP or tar.gz (new endpoint). The
// browser cannot navigate to these URLs directly because the bearer
// token would be dropped, so each fetches the bytes and saves them via an
// object URL. Only one download runs at a time; the others disable while
// one is in flight.
function DownloadsPanel(props: { receiptId: string }) {
  const { receiptId } = props;
  const [busy, setBusy] = useState<DownloadKind | null>(null);
  const [lastKind, setLastKind] = useState<DownloadKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function run(kind: DownloadKind): void {
    setBusy(kind);
    setLastKind(kind);
    setError(null);
    void (async () => {
      try {
        const { blob, filename } =
          kind === "envelope"
            ? await downloadReceiptEnvelope(receiptId)
            : await downloadReceiptPackage(receiptId, kind);
        saveBlob(blob, filename);
      } catch (err) {
        if (err instanceof PackageDownloadError || err instanceof EnvelopeDownloadError) {
          setError(`${err.code}: ${err.message}`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Download failed.");
        }
      } finally {
        setBusy(null);
      }
    })();
  }

  const disabled = busy !== null;
  return (
    <div className="panel">
      <h2 className="section-title">Downloads</h2>
      <div className="receipt-action-row">
        <button className="btn btn-secondary" disabled={disabled} onClick={() => run("envelope")}>
          {busy === "envelope" ? "Preparing..." : "Download envelope (.json)"}
        </button>
        <button className="btn btn-secondary" disabled={disabled} onClick={() => run("zip")}>
          {busy === "zip" ? "Preparing..." : "Download package (.sequesign.zip)"}
        </button>
        <button className="btn btn-secondary" disabled={disabled} onClick={() => run("tar.gz")}>
          {busy === "tar.gz" ? "Preparing..." : "Download package (.sequesign.tar.gz)"}
        </button>
      </div>
      {error !== null && (
        <div className="error" style={{ marginTop: 8 }}>
          {error}
          {lastKind !== null && (
            <button
              className="btn btn-secondary"
              style={{ marginLeft: 8 }}
              disabled={disabled}
              onClick={() => run(lastKind)}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function ReceiptDetailPage(props: { receiptId: string; onBack: () => void }) {
  const { receiptId, onBack } = props;
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      try {
        const envelope = await api.getReceipt(receiptId);
        if (cancelled) return;
        setState({ kind: "ready", envelope });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setState({ kind: "missing" });
            return;
          }
          if (err.status === 410) {
            setState({ kind: "not_stored" });
            return;
          }
          if (err.status === 503) {
            setState({ kind: "not_configured" });
            return;
          }
          setState({ kind: "error", message: err.message || "Could not load receipt." });
          return;
        }
        const message = err instanceof Error ? err.message : "Could not load receipt.";
        setState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [receiptId]);

  return (
    <>
      <div className="panel">
        <a
          href="/receipts"
          className="receipt-back-link"
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          ← Back to receipts
        </a>
        {renderHeader(receiptId, state)}
      </div>
      <RetentionPanel receiptId={receiptId} />
      {renderBody(state, receiptId)}
    </>
  );
}

// §3.1 Retention PR 2: per-receipt retention state and extend control.
// Loads independently from the envelope (different sources: DB row vs
// R2 object) so a missing R2 object does not block the retention view.
function RetentionPanel(props: { receiptId: string }) {
  const { receiptId } = props;
  const [data, setData] = useState<ReceiptRetention | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [until, setUntil] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  async function reload(): Promise<void> {
    try {
      const result = await api.getReceiptRetention(receiptId);
      setData(result);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not load retention state.");
    }
  }

  useEffect(() => {
    void reload();
  }, [receiptId]);

  async function submitExtend(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setMessage(null);
    try {
      const result = await api.extendReceipt(receiptId, { until });
      setMessage(`Extended to ${result.retention_until}.`);
      setUntil("");
      await reload();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "retention_override_too_short") {
          setMessage("That date is shorter than the current retention; pick a later date.");
        } else {
          setMessage(err.message);
        }
      } else {
        setMessage("Could not extend retention.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (error && !data) {
    return (
      <div className="panel">
        <h2 className="section-title">Retention</h2>
        <div className="error">{error}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="panel">
        <h2 className="section-title">Retention</h2>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  const retentionLabel =
    data.retention_until === "infinity" ? "Indefinite (no auto-prune)" : data.retention_until;
  const isTombstoned = data.deleted_at !== null;
  const isHardDeleted = data.permanently_deleted_at !== null;
  const isHeld = data.hold_reason !== null && data.hold_reason.length > 0;

  return (
    <div className="panel">
      <h2 className="section-title">Retention</h2>
      <dl className="row-grid">
        <Row label="Retention until">
          <code>{retentionLabel}</code>
        </Row>
        {data.tier_at_creation !== null && (
          <Row label="Tier at creation">
            <code>{data.tier_at_creation}</code>
          </Row>
        )}
        {isHeld && (
          <Row label="Hold reason">
            <code>{data.hold_reason}</code>
          </Row>
        )}
        {isTombstoned && (
          <Row label="Tombstoned at">
            <code>{data.deleted_at}</code>
          </Row>
        )}
        {isHardDeleted && (
          <Row label="Permanently deleted at">
            <code>{data.permanently_deleted_at}</code>
          </Row>
        )}
      </dl>
      {!isTombstoned && !isHardDeleted && (
        <form onSubmit={submitExtend} style={{ marginTop: 16 }}>
          <h3 className="section-title" style={{ fontSize: 14 }}>
            Extend this receipt
          </h3>
          <p className="muted" style={{ fontSize: 12 }}>
            New retention must be at or after the current value. Use <code>infinity</code> to remove
            auto-prune for this receipt.
          </p>
          {/* PR 13.5: the input + button row uses flex, not the page
              global ".row" class, because ".row" is grid 200px 1fr and
              squeezes the input into a 200px column while stretching
              the button. flex with a fixed-content button and a
              flex:1 input gives a usable input width and a content-
              sized button. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 8
            }}
          >
            <input
              type="text"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              placeholder="ISO timestamp or 'infinity'"
              style={{ flex: 1, minWidth: 0 }}
            />
            <button
              className="btn"
              type="submit"
              disabled={submitting || until.length === 0}
              style={{ flex: "0 0 auto" }}
            >
              {submitting ? "Saving..." : "Extend"}
            </button>
          </div>
          {message !== null && (
            <div
              className={message.startsWith("Error") ? "error" : "muted"}
              style={{ marginTop: 8 }}
            >
              {message}
            </div>
          )}
        </form>
      )}
    </div>
  );
}

function renderHeader(receiptId: string, state: LoadState) {
  if (state.kind === "ready") {
    const { envelope } = state;
    return (
      <>
        <div className="section-header">
          <h2 className="section-title">Receipt</h2>
          <div className="receipt-action-row" aria-hidden="true">
            {/* Download and verifier buttons land in PR C; the row is
                rendered now so the layout does not jump when they
                arrive. */}
          </div>
        </div>
        <dl className="row-grid">
          <Row label="Receipt id">
            <code>{envelope.receipt_id}</code>
          </Row>
          <Row label="Chain id">
            <code>{envelope.chain.chain_id}</code>
          </Row>
          <Row label="Receipt mode">
            <code>{envelope.receipt_mode}</code>
          </Row>
          <Row label="Verification level">
            <VerificationBadges envelope={envelope} />
          </Row>
          <Row label="Schema version">
            <code>{envelope.schema_version}</code>
          </Row>
        </dl>
        <VerificationLevelLegend />
      </>
    );
  }
  return (
    <>
      <div className="section-header">
        <h2 className="section-title">Receipt</h2>
      </div>
      <dl className="row-grid">
        <Row label="Receipt id">
          <code>{receiptId}</code>
        </Row>
      </dl>
    </>
  );
}

function renderBody(state: LoadState, receiptId: string) {
  if (state.kind === "loading") {
    return (
      <div className="panel">
        <p className="muted">Loading receipt…</p>
      </div>
    );
  }
  if (state.kind === "missing") {
    return (
      <div className="panel">
        <div className="error">
          Receipt not found. It may have been removed, or it belongs to a different account.
        </div>
      </div>
    );
  }
  if (state.kind === "not_stored") {
    return (
      <div className="panel">
        <div className="error">
          This receipt's envelope is not stored at Sequesign. The customer holds the envelope; only
          the witness attestation is recorded here.
        </div>
      </div>
    );
  }
  if (state.kind === "not_configured") {
    return (
      <div className="panel">
        <div className="error">
          Receipt storage is not yet configured for this environment. Receipts will appear here once
          the storage layer is enabled.
        </div>
      </div>
    );
  }
  if (state.kind === "error") {
    return (
      <div className="panel">
        <div className="error">{state.message}</div>
      </div>
    );
  }
  const { envelope } = state;
  return (
    <>
      <DownloadsPanel receiptId={receiptId} />
      <div className="panel">
        <h2 className="section-title">Summary</h2>
        <dl className="row-grid">
          <Row label="Task delegated at">
            <span title={envelope.task.delegated_at}>
              {formatTimestamp(envelope.task.delegated_at)}
            </span>
          </Row>
          <Row label="Final chain state">
            <code title={envelope.chain.final_chain_state}>
              {truncateHash(envelope.chain.final_chain_state)}
            </code>
          </Row>
          <Row label="Sequence start">
            <code>{envelope.chain.sequence_start}</code>
          </Row>
          <Row label="Final action count">
            <code>{envelope.action_record_hashes.length}</code>
          </Row>
          <Row label="Witness">
            <code>{summarizeWitnesses(envelope)}</code>
          </Row>
        </dl>
        <h3 className="receipt-subheader">Actions</h3>
        {envelope.agent_attestations.length === 0 ? (
          <p className="muted">No action attestations in this envelope.</p>
        ) : (
          <ol className="receipt-action-list">
            {envelope.agent_attestations.map((att) => (
              <li key={`${att.chain_id}-${att.sequence}`}>
                <code className="receipt-action-seq">#{att.sequence}</code>
                <code className="muted-code receipt-action-hash">
                  {truncateHash(att.action_record_hash)}
                </code>
              </li>
            ))}
          </ol>
        )}
        <h3 className="receipt-subheader">Verification surface</h3>
        <FlagList envelope={envelope} />
      </div>
      <div className="panel">
        <h2 className="section-title">Evidence</h2>
        {envelope.evidence_references.length === 0 ? (
          <p className="muted">No evidence references in this envelope.</p>
        ) : (
          <div className="evidence-list">
            {envelope.evidence_references.map((ref) => (
              <EvidencePanel key={ref.action_id} receiptId={receiptId} reference={ref} />
            ))}
          </div>
        )}
      </div>
      <div className="panel">
        <h2 className="section-title">Raw envelope</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          The envelope as stored, pretty-printed. Use the (forthcoming) download button to save the
          exact bytes for offline verification.
        </p>
        <pre className="receipt-json">
          <code>{JSON.stringify(envelope, null, 2)}</code>
        </pre>
      </div>
    </>
  );
}

type EvidenceState =
  | { kind: "collapsed" }
  | { kind: "loading" }
  | {
      kind: "loaded";
      content: unknown;
      hashVerified: boolean;
      computedHash: string;
      expectedHash: string;
    }
  | { kind: "error"; message: string };

function EvidencePanel(props: { receiptId: string; reference: EvidenceReference }) {
  const { receiptId, reference } = props;
  const [state, setState] = useState<EvidenceState>({ kind: "collapsed" });

  const hosted = evidenceIsFetchable(reference);

  async function load() {
    setState({ kind: "loading" });
    try {
      const { content, bytes } = await fetchEvidenceContent(receiptId, reference.action_id);
      const { verified, computedHash } = await verifyEvidenceBytes(bytes, reference.evidence_hash);
      setState({
        kind: "loaded",
        content,
        hashVerified: verified,
        computedHash,
        expectedHash: reference.evidence_hash
      });
    } catch (err) {
      const message =
        err instanceof EvidenceFetchError
          ? err.message || `Could not load evidence (${err.code}).`
          : err instanceof Error
            ? err.message
            : "Could not load evidence.";
      setState({ kind: "error", message });
    }
  }

  function download() {
    // The download path reuses the same endpoint with download=1, which
    // sets Content-Disposition server-side. Navigating the browser to
    // the URL would drop the bearer token, so fetch the bytes and save
    // them via an object URL instead.
    void (async () => {
      try {
        const { bytes } = await fetchEvidenceContent(receiptId, reference.action_id, {
          download: true
        });
        const blob = new Blob([bytes as BlobPart], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = basenameOf(reference.evidence_path);
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch {
        // A failed download leaves the inline view intact; the user can
        // retry. Surfacing a toast is out of scope for this panel.
      }
    })();
  }

  return (
    <div className="evidence-item">
      <div className="evidence-item-header">
        <code className="evidence-action-id">{reference.action_id}</code>
        <div className="evidence-meta">
          <span className="muted">Evidence hash</span>
          <code title={reference.evidence_hash}>{truncateHash(reference.evidence_hash)}</code>
          <span className="muted">Custody</span>
          <span>{hosted ? "Sequesign hosted" : "External (client-managed)"}</span>
          <span className="muted">Path</span>
          <code className="muted-code">{reference.evidence_path}</code>
        </div>
        <div className="evidence-actions">
          {hosted ? (
            state.kind === "loaded" ? (
              <>
                <button
                  className="btn btn-secondary"
                  onClick={() => setState({ kind: "collapsed" })}
                >
                  Hide
                </button>
                <button className="btn btn-secondary" onClick={download}>
                  Download .json
                </button>
              </>
            ) : state.kind === "loading" ? (
              <span className="muted">Loading…</span>
            ) : (
              <button className="btn btn-secondary" onClick={load}>
                Show evidence
              </button>
            )
          ) : null}
        </div>
      </div>
      {hosted ? (
        <EvidenceBody state={state} onRetry={load} />
      ) : (
        <p className="evidence-external-note">
          This evidence is held externally by the client. Sequesign does not have the content.
        </p>
      )}
    </div>
  );
}

function EvidenceBody(props: { state: EvidenceState; onRetry: () => void }) {
  const { state, onRetry } = props;
  if (state.kind === "collapsed" || state.kind === "loading") {
    return null;
  }
  if (state.kind === "error") {
    return (
      <div className="evidence-expanded">
        <div className="error">{state.message}</div>
        <button className="btn btn-secondary" style={{ marginTop: 8 }} onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }
  return (
    <div className="evidence-expanded">
      {state.hashVerified ? (
        <div className="evidence-hash-ok">Hash verified</div>
      ) : (
        <div className="evidence-hash-mismatch">
          <strong>Hash mismatch.</strong> The stored evidence does not match the hash recorded in
          the receipt. This indicates storage tampering or a verifier bug.
          <div className="evidence-hash-detail">
            <span className="muted">Expected</span>
            <code>{state.expectedHash}</code>
            <span className="muted">Computed</span>
            <code>{state.computedHash}</code>
          </div>
        </div>
      )}
      <pre className="receipt-json">
        <code>{JSON.stringify(state.content, null, 2)}</code>
      </pre>
    </div>
  );
}

function basenameOf(p: string): string {
  const i = p.lastIndexOf("/");
  return i === -1 ? p : p.slice(i + 1);
}

// Two independent badges for the verification result: the witnessed
// axis and the ladder level. Per protocol-spec.md 6.3 these are distinct
// claims and MUST NOT be collapsed into one ordinal label, so they render
// side by side. Both witnessed states are shown (a not-witnessed receipt
// is not left blank) so the absence of witnessing is explicit. The level
// is inferred from envelope contents and stays "pending verifier check"
// until the cryptographic verifier runs, so the muted note is preserved.
function VerificationBadges(props: { envelope: ReceiptEnvelope }) {
  const witnessed = isWitnessed(props.envelope);
  const level = inferVerificationLevel(props.envelope);
  return (
    <span className="verification-badges">
      <span className={`badge ${witnessed ? "badge-witnessed" : "badge-unwitnessed"}`}>
        {witnessedBadgeLabel(witnessed)}
      </span>
      <span className="badge badge-level">{levelBadgeLabel(level)}</span>
      <span className="muted verification-badges-note">pending verifier check</span>
    </span>
  );
}

function Row(props: { label: string; children: React.ReactNode }) {
  return (
    <div className="row">
      <dt>{props.label}</dt>
      <dd>{props.children}</dd>
    </div>
  );
}

function FlagList(props: { envelope: ReceiptEnvelope }) {
  const flags = describeFlags(props.envelope);
  return (
    <ul className="receipt-flag-list">
      {flags.map((flag) => (
        <li key={flag.name}>
          <span className={`flag-status flag-${flag.status}`}>
            {flag.status === "PASSED"
              ? "PASSED"
              : flag.status === "NOT_REQUESTED"
                ? "NOT REQUESTED"
                : "NOT YET VERIFIED"}
          </span>
          <span className="flag-name">{flag.name}</span>
          {flag.note ? <span className="muted flag-note">{flag.note}</span> : null}
        </li>
      ))}
    </ul>
  );
}

type FlagDescriptor = {
  name: string;
  status: "PASSED" | "NOT_REQUESTED" | "PENDING";
  note?: string;
};

// Derives a flag summary from the envelope contents. This is descriptive,
// not authoritative; the real verifier still has to run to confirm the
// flags actually hold. The point here is to give the customer a
// preview of what verification will report.
function describeFlags(envelope: ReceiptEnvelope): FlagDescriptor[] {
  const flags: FlagDescriptor[] = [];
  flags.push({
    name: "Witnessed",
    status: envelope.witness_attestations.length > 0 ? "PASSED" : "PENDING",
    note:
      envelope.witness_attestations.length > 0
        ? `${envelope.witness_attestations.length} attestations`
        : "No witness attestations present"
  });
  flags.push({
    name: "Schema validated",
    status:
      envelope.schema_references && envelope.schema_references.length > 0
        ? "PASSED"
        : "NOT_REQUESTED",
    note:
      envelope.schema_references && envelope.schema_references.length > 0
        ? `${envelope.schema_references.length} schema(s) bound`
        : "Freeform receipt; no schema bound"
  });
  flags.push({
    name: "Workflow template",
    status: envelope.profile ? "PASSED" : "NOT_REQUESTED",
    note: envelope.profile ? envelope.profile.profile_id : "No template bound"
  });
  // v0.6 arc: approval and counterparty are independent tri-state badges
  // (party_type may be human OR agent, so the copy stays neutral). They are
  // present-but-unverified until the signer's identity is vouched, so a
  // present badge previews as PENDING ("not yet verified"), never PASSED —
  // the dashboard cannot vouch identity, and overstating it would mislead.
  const approvalCount = envelope.approval_attestations.length;
  flags.push({
    name: "Approval",
    status: hasApproval(envelope) ? "PENDING" : "NOT_REQUESTED",
    note: hasApproval(envelope)
      ? `${approvalCount} attestation(s) present; signer identity not yet vouched`
      : undefined
  });
  const counterpartyCount = (envelope.counterparty_attestations ?? []).length;
  flags.push({
    name: "Counterparty",
    status: hasCounterparty(envelope) ? "PENDING" : "NOT_REQUESTED",
    note: hasCounterparty(envelope)
      ? `${counterpartyCount} attestation(s) present; signer identity not yet vouched`
      : undefined
  });
  return flags;
}

function summarizeWitnesses(envelope: ReceiptEnvelope): string {
  const ids = new Set<string>();
  for (const att of envelope.witness_attestations) {
    if (att.witness_id) ids.add(att.witness_id);
  }
  if (ids.size === 0) return "(no witness attestations)";
  return [...ids].join(", ");
}

function truncateHash(value: string): string {
  if (value.length <= 18) return value;
  return `${value.slice(0, 8)}…${value.slice(-8)}`;
}

function formatTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  return new Date(parsed).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
